import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import ServicingApi from "../../services/AssociatedServices";
import { FadeLoader } from "react-spinners";
import ServiveAndCustomerInfo from "./ServiceAndCustomerInfo";
import translations from "../../assets/locals/translations";
import "./serviceMenu.css";

// Predefined color schemes to be used cyclically
const colorSchemes = [
  {
    backgroundColor: "#EFF6FF",
    borderLeft: "4px solid #3B82F6",
    textColor: "#1D4ED8",
  },
  {
    backgroundColor: "#FAF5FF",
    borderLeft: "4px solid #C084FC",
    textColor: "#9333EA",
  },
  {
    backgroundColor: "#FEFCE8",
    borderLeft: "4px solid #FACC15",
    textColor: "#CA8A04",
  },
  {
    backgroundColor: "#F0FDF4",
    borderLeft: "4px solid #34D399",
    textColor: "#059669",
  },
  {
    backgroundColor: "#FFF5F5",
    borderLeft: "4px solid #F87171",
    textColor: "#DC2626",
  },
  {
    backgroundColor: "#FFFAF0",
    borderLeft: "4px solid #FBBF24",
    textColor: "#D97706",
  },
  {
    backgroundColor: "#F3F4F6",
    borderLeft: "4px solid #9CA3AF",
    textColor: "#6B7280",
  },
  {
    backgroundColor: "#E0F7FA",
    borderLeft: "4px solid #00ACC1",
    textColor: "#007C91",
  },
  {
    backgroundColor: "#E0F2F1",
    borderLeft: "4px solid #26A69A",
    textColor: "#00796B",
  },
  {
    backgroundColor: "#FCE4EC",
    borderLeft: "4px solid #EC407A",
    textColor: "#C2185B",
  },
  {
    backgroundColor: "#E8EAF6",
    borderLeft: "4px solid #5C6BC0",
    textColor: "#3949AB",
  },
];

const ServicesMenu = () => {
  const navigate = useNavigate();
  const currentLanguage = useSelector((state) => state.language.language);
  const t = translations[currentLanguage] || translations['en'];
  const [servicesData, setServicesData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("home"); // Track active tab
  const services = new ServicingApi();

  // Global search state (if needed elsewhere)
  const [searchQuery, setSearchQuery] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getServicingList = async () => {
    setLoading(true);
    try {
      const response = await services.getServicingList();
      if (response && response.isSuccess) {
        const fetchedData = response.result.map((item, index) => {
          return {
            service_id: item.id,
            title: item.name,
            subTitle: item.description,
            hours_threshold: item.hours_threshold,
            days_threshold: item.days_threshold,
            ...colorSchemes[index % colorSchemes.length],
          };
        });

        // Set basic data first so UI shows quickly
        setServicesData(fetchedData);

        // Then fetch counts for each service card
        try {
          const dataWithCounts = await Promise.all(
            fetchedData.map(async (service) => {
              try {
                const countResponse = await services.getServicingVehicles({
                  service_id: service.service_id,
                  hours_threshold: service.hours_threshold,
                  days_threshold: service.days_threshold,
                });

                if (
                  countResponse &&
                  countResponse.isSuccess &&
                  Array.isArray(countResponse.result) &&
                  countResponse.result.length > 0
                ) {
                  const firstRow = countResponse.result[0];
                  return {
                    ...service,
                    delivery_count: firstRow.delivery_count || 0,
                    itl_count: firstRow.itl_count || 0,
                  };
                }
              } catch (err) {
                console.error(
                  "Error fetching counts for service:",
                  service.service_id,
                  err
                );
              }

              return {
                ...service,
                delivery_count: 0,
                itl_count: 0,
              };
            })
          );

          setServicesData(dataWithCounts);
        } catch (err) {
          console.error("Error enriching services with counts:", err);
        }
      }
    } catch (error) {
      console.error("Error getting servicing list:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getServicingList();
  }, []);

  const handleCardClick = (service) => {
    // Extract and format the hour portion from the title if applicable
    const hourPortion = service.title.split(" / ")[0].trim();
    const typeParam = hourPortion.toLowerCase().replace(/\s+/g, "-");
    navigate(`/service/servicing/${typeParam}`, { state: { item: service } });
  };

  return (
    <div className="bg-white">
      <div>
        <ul
          className="nav nav-pills mb-3 justify-content-center justify-content-md-start custom-nav-pills"
          role="tablist"
        >
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === "home" ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => setActiveTab("home")}
            >
              {t.serviceDetails || "Service Details"}
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === "profile" ? "active" : ""}`}
              type="button"
              role="tab"
              onClick={() => setActiveTab("profile")}
            >
              {t.serviceAndCustomerInfo || "Service & Customer Info"}
            </button>
          </li>
        </ul>
        <div className="tab-content" id="pills-tabContent">
          {activeTab === "home" && (
            <div className="tab-pane fade show active" role="tabpanel">
              <Box sx={{ mx: "auto", paddingX: 2, paddingBottom: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: "bold", mr: 2 }}>
                    {t.associatedServices || "Associated Services"}
                  </Typography>
                  <Box sx={{ flexGrow: 1, borderBottom: "1px solid #ccc" }} />
                </Box>
                {loading ? (
                  <div
                    className="container"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}

                  >
                    <FadeLoader size={10} color="#808b96" />
                    <span
                      style={{
                        fontFamily: "Poppins, sans-serif",
                        fontSize: "16px",
                        color: "#808b96",
                      }}
                    >
                      {t.loadingPleaseWait || "Loading, please wait..."}
                    </span>
                  </div>
                ) : servicesData.length === 0 ? (
                  <span
                    className="container"
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      textAlign: "center",
                    }}
                  >
                    {t.noServicesAvailable || "No Services Available"}
                  </span>
                ) : (
                  <Grid container spacing={2}>
                    {servicesData.map((item, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card
                          elevation={0}
                          sx={{
                            backgroundColor: item.backgroundColor,
                            borderLeft: item.borderLeft,
                            borderRadius: 2,
                            cursor: "pointer",
                          }}
                          onClick={() => handleCardClick(item)}
                        >
                          <CardContent>
                            <Typography
                              variant="subtitle1"
                              sx={{
                                color: item.textColor,
                                fontWeight: 600,
                                mb: 1,
                              }}
                            >
                              {item.title}
                            </Typography>
                            {item.subTitle && (
                              <Typography
                                variant="body2"
                                sx={{ color: "#4B5563" }}
                              >
                                {item.subTitle}
                              </Typography>
                            )}
                            {item.subTitle && (
                              <Typography
                                variant="body2"
                                sx={{ color: "#4B5563", }}
                              >
                                <Typography
                                  component="span"
                                  sx={{
                                    fontSize: "1.25rem",
                                    fontWeight: 700,
                                  }}
                                >
                                  {item.service_id}
                                </Typography>
                                <Typography
                                  component="span"
                                  sx={{ fontSize: "0.75rem", fontWeight: 400 }}
                                >
                                  {t.thService || "th Service"}
                                </Typography>
                              </Typography>
                            )}
                            <Typography
                              variant="body2"
                              sx={{ color: "#4B5563", mt: 0.5 }}
                            >
                              {(t.delivery || "Delivery") +
                                ": " +
                                (item.delivery_count ?? 0)}
                              {" | ITL: " + (item.itl_count ?? 0)}
                            </Typography>
                            {item.vehicles && (
                              <Typography
                                variant="body2"
                                sx={{ color: "#4B5563" }}
                              >
                                {item.vehicles}
                              </Typography>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </div>
          )}
          {activeTab === "profile" && (
            <div className="tab-pane fade show active" role="tabpanel">
              {/* Only mounts when the Service & Customer Info tab is active */}
              <ServiveAndCustomerInfo />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ServicesMenu;
